/**
 * Helper functions for Doctor Recipe API
 * Reusable utilities for FormData building and response extraction
 */

/**
 * Adds a field to FormData only if the value exists (not undefined or null)
 */
export const appendIfExists = (
  formData: FormData,
  key: string,
  value: string | number | File | undefined | null,
): void => {
  if (value !== undefined && value !== null) {
    formData.append(key, typeof value === 'number' ? String(value) : value);
  }
};

/**
 * Adds an array to FormData as JSON stringified value, only if array exists and is not empty
 */
export const appendIfArrayNotEmpty = (
  formData: FormData,
  key: string,
  array: unknown[] | undefined,
): void => {
  if (array && array.length > 0) {
    formData.append(key, JSON.stringify(array));
  }
};

/**
 * Extracts the actual data from backend response
 * Backend wraps responses in: { success, message, data: {...} }
 * This function extracts the nested data property
 */
export const extractResponseData = <T>(response: {
  data?: { data?: T; [key: string]: unknown } | T;
}): T => {
  const responseData = response.data;
  if (
    responseData &&
    typeof responseData === 'object' &&
    'data' in responseData &&
    responseData.data !== undefined
  ) {
    return responseData.data as T;
  }
  return responseData as T;
};

/**
 * Builds FormData for recipe create/update operations
 * Handles common fields and array format differences between POST and PUT
 * @param data - Remedy data
 * @param arrayFormat - 'json' for POST (JSON stringified) or 'form-fields' for PUT (multiple form fields)
 * @returns FormData object ready to send
 */
export const buildRecipeFormData = (
  data: {
    name?: string; // internal_name
    title: string;
    description?: string;
    category_id?: string | number;
    category_ids?: Array<string | number>;
    dosha_ids?: number[];
    season?: string[];
    season_ids?: Array<string | number>;
    region_ids?: Array<string | number>;
    gender_ids?: Array<string | number>;
    age_group_ids?: Array<string | number>;
    tags?: Array<string | number>; // Frontend tag names (can be IDs or names)
    dietaryType?: string; // Legacy
    helpsWithDiseases?: Array<string | number>; // symptom_ids
    helpsWithDiseasesOther?: string;
    contra_indication_ids?: Array<string | number>;
    contraindications?: Array<string | number>;
    warningsPrecautions?: string;
    side_effects?: string;
    preparation_time?: number;
    servings?: number;
    calorie?: number;
    warnings?: string;
    nutritional_info?: string;
    cover_image?: File;
    cover_gallery_images?: File[];
    existing_cover_gallery_urls?: string[];
    existing_cover_image_url?: string | null;
    cover_image_url?: string | null;
    cover_video_url?: string | null;
    preparation_video_url?: string | null;
    cover_gallery_image_urls?: string[];
    deleted_cover_gallery_urls?: string[];
    cover_video?: File;
    preparation_video?: File;

    // Dosage & New
    dosage_qty?: string | number;
    dosage_instructions?: string;
    unit_id?: number | string;
    frequency_id?: number | string;
    timing_id?: number | string;
    meal_timing_id?: number | string;
    duration_days?: number | string;
    duration_unit_id?: number | string;
    max_limit_days?: number | string;
    max_duration_unit_id?: number | string;
    apply_for_value?: number | string | null;
    apply_for_uom?: string | null;
    common_mistakes_json?: unknown[] | string;
    q_and_a_json?: unknown[] | string;
    ref_links?: unknown[] | string;
    suitability_json?: unknown[] | string;
    recommended_diet?: string;
    diet_to_avoid?: string;
    general_exercise_notes?: string;
    custom_list_values?: Record<string, string[]>;
    exercises?: Array<{
      exercise_id?: string | number;
      exercise_type_id?: string | number;
      note?: string;
      name?: string;
    }>;
    // Helper
    parent_id?: string | number | null;
    is_variation?: boolean;
    medical_conditions_grid?: string | unknown[]; // JSON string or array
    natural_condition_ids?: Array<string | number>;
    // New grids / optional fields
    symptoms_grid?: unknown[] | string;
    contraindications_grid?: unknown[] | string;
    how_many_days?: number | string;
    gap_days?: number | string;
    how_many_days_unit_id?: number | string;
    gap_days_unit_id?: number | string;
    contraindications_notes?: string;
    is_draft?: boolean;
    delete_cover_image?: boolean;
    delete_cover_video?: boolean;
    delete_preparation_video?: boolean;
    ingredients?: Array<{
      ingredient_name?: string;
      name?: string;
      quantity?: string | number;
      unit?: string;
    }>;
    preparation_steps?: Array<{
      step_number?: number;
      title?: string;
      description?: string;
    }>;
  },
  arrayFormat: 'json' | 'form-fields' = 'json',
): FormData => {
  const formData = new FormData();
  const normalizedCoverImage =
    data.cover_image instanceof File
      ? data.cover_image
      : data.cover_image &&
          typeof data.cover_image === 'object' &&
          'length' in data.cover_image &&
          typeof (data.cover_image as { length?: number }).length === 'number'
        ? ((data.cover_image as unknown as FileList)[0] ?? undefined)
        : undefined;

  // Debug: Log contraindications data
  console.log('🔍 DEBUG buildRecipeFormData - Input:', {
    contra_indication_ids: data.contra_indication_ids,
    contraindications: data.contraindications,
    arrayFormat,
  });

  // Add required text field
  formData.append('title', data.title);

  // Add new fields
  appendIfExists(formData, 'name', data.name);
  appendIfExists(formData, 'internal_name', data.name); // Also support backend field name
  // Always append helpsWithDiseasesOther, even if empty string (to ensure it's not null)
  // Backend expects empty string, not null, when field is not filled
  if (
    data.helpsWithDiseasesOther !== undefined &&
    data.helpsWithDiseasesOther !== null
  ) {
    formData.append(
      'helpsWithDiseasesOther',
      String(data.helpsWithDiseasesOther),
    );
  } else {
    // If undefined or null, send empty string
    formData.append('helpsWithDiseasesOther', '');
  }
  appendIfExists(formData, 'warningsPrecautions', data.warningsPrecautions);
  appendIfExists(formData, 'dietaryType', data.dietaryType);
  appendIfExists(formData, 'side_effects', data.side_effects);
  appendIfExists(formData, 'parent_id', data.parent_id);

  if (data.is_variation !== undefined) {
    formData.append('is_variation', String(data.is_variation));
  }

  // Handle is_draft if present
  if (data.is_draft !== undefined) {
    formData.append('is_draft', String(data.is_draft));
  }

  // Add files (now optional)
  appendIfExists(formData, 'cover_image', normalizedCoverImage);
  if (
    !normalizedCoverImage &&
    typeof data.existing_cover_image_url === 'string' &&
    data.existing_cover_image_url.trim()
  ) {
    const u = data.existing_cover_image_url.trim();
    const normalizedCoverUrl = (() => {
      try {
        return new URL(u).origin + new URL(u).pathname;
      } catch {
        return u.split('?')[0];
      }
    })();
    formData.append('existing_cover_image_url', normalizedCoverUrl);
  }
  if (typeof data.cover_image_url === 'string' && data.cover_image_url.trim()) {
    formData.append(
      'cover_image_url',
      data.cover_image_url.trim().split('?')[0],
    );
  }
  if (Array.isArray(data.cover_gallery_images)) {
    data.cover_gallery_images
      .filter((f): f is File => f instanceof File && f !== normalizedCoverImage)
      .forEach((file) => {
        formData.append('cover_gallery_images', file);
      });
  }
  if (Array.isArray(data.existing_cover_gallery_urls)) {
    const normalizedExistingGalleryUrls = data.existing_cover_gallery_urls
      .filter((url): url is string => typeof url === 'string' && !!url.trim())
      .map((url) => {
        try {
          return new URL(url).origin + new URL(url).pathname;
        } catch {
          return url.split('?')[0];
        }
      });
    formData.append(
      'existing_cover_gallery_urls',
      JSON.stringify(normalizedExistingGalleryUrls),
    );
  }
  if (Array.isArray(data.deleted_cover_gallery_urls)) {
    const normalizedDeletedGalleryUrls = data.deleted_cover_gallery_urls
      .filter((url): url is string => typeof url === 'string' && !!url.trim())
      .map((url) => {
        try {
          return new URL(url).origin + new URL(url).pathname;
        } catch {
          return url.split('?')[0];
        }
      });
    formData.append(
      'deleted_cover_gallery_urls',
      JSON.stringify(normalizedDeletedGalleryUrls),
    );
  }
  appendIfExists(formData, 'cover_video', data.cover_video);
  appendIfExists(formData, 'preparation_video', data.preparation_video);
  if (typeof data.cover_video_url === 'string' && data.cover_video_url.trim()) {
    formData.append(
      'cover_video_url',
      data.cover_video_url.trim().split('?')[0],
    );
  }
  if (
    typeof data.preparation_video_url === 'string' &&
    data.preparation_video_url.trim()
  ) {
    formData.append(
      'preparation_video_url',
      data.preparation_video_url.trim().split('?')[0],
    );
  }
  if (Array.isArray(data.cover_gallery_image_urls)) {
    const normalized = data.cover_gallery_image_urls
      .filter((u): u is string => typeof u === 'string' && !!u.trim())
      .map((u) => u.trim().split('?')[0]);
    if (normalized.length > 0) {
      formData.append('cover_gallery_image_urls', JSON.stringify(normalized));
    }
  }

  // Add deletion flags
  if (data.delete_cover_image) {
    formData.append('delete_cover_image', 'true');
  }
  if (data.delete_cover_video) {
    formData.append('delete_cover_video', 'true');
  }
  if (data.delete_preparation_video) {
    formData.append('delete_preparation_video', 'true');
  }

  // Add optional text fields
  appendIfExists(formData, 'description', data.description);
  appendIfExists(formData, 'category_id', data.category_id);
  appendIfExists(formData, 'warnings', data.warnings);
  appendIfExists(formData, 'nutritional_info', data.nutritional_info);

  // Dosage Fields
  appendIfExists(formData, 'dosage_qty', data.dosage_qty);
  appendIfExists(formData, 'dosage_instructions', data.dosage_instructions);
  appendIfExists(formData, 'unit_id', data.unit_id);
  appendIfExists(formData, 'frequency_id', data.frequency_id);
  appendIfExists(formData, 'timing_id', data.timing_id);
  appendIfExists(formData, 'meal_timing_id', data.meal_timing_id);
  appendIfExists(formData, 'duration_days', data.duration_days);
  appendIfExists(formData, 'duration_unit_id', data.duration_unit_id);
  appendIfExists(formData, 'max_limit_days', data.max_limit_days);
  appendIfExists(formData, 'max_duration_unit_id', data.max_duration_unit_id);
  formData.append(
    'apply_for_value',
    data.apply_for_value != null && data.apply_for_value !== ''
      ? String(data.apply_for_value)
      : '',
  );
  formData.append(
    'apply_for_uom',
    data.apply_for_uom ? String(data.apply_for_uom) : '',
  );
  appendIfExists(formData, 'how_many_days', data.how_many_days);
  appendIfExists(formData, 'gap_days', data.gap_days);

  // Gap UOM fields - always append so backend receives them (prevents null overwrite)
  formData.append(
    'how_many_days_unit_id',
    String(data.how_many_days_unit_id ?? ''),
  );
  formData.append('gap_days_unit_id', String(data.gap_days_unit_id ?? ''));

  // Contraindications notes (persist + allow clearing)
  formData.append(
    'contraindications_notes',
    data.contraindications_notes ?? '',
  );

  // Grids (persisted as JSON)
  if (data.symptoms_grid !== undefined) {
    if (typeof data.symptoms_grid === 'string') {
      appendIfExists(formData, 'symptoms_grid', data.symptoms_grid);
    } else if (Array.isArray(data.symptoms_grid)) {
      formData.append('symptoms_grid', JSON.stringify(data.symptoms_grid));
    }
  }
  if (data.contraindications_grid !== undefined) {
    if (typeof data.contraindications_grid === 'string') {
      appendIfExists(
        formData,
        'contraindications_grid',
        data.contraindications_grid,
      );
    } else if (Array.isArray(data.contraindications_grid)) {
      formData.append(
        'contraindications_grid',
        JSON.stringify(data.contraindications_grid),
      );
    }
  }

  // Always send diet fields so backend persists them (including empty = clear)
  formData.append('recommended_diet', data.recommended_diet ?? '');
  formData.append('diet_to_avoid', data.diet_to_avoid ?? '');
  formData.append('general_exercise_notes', data.general_exercise_notes ?? '');

  // Custom free-text list entries (incl. custom exercises) — always send so clears persist
  formData.append(
    'custom_list_values',
    JSON.stringify(data.custom_list_values ?? {}),
  );

  // Exercises - only pass when BOTH exercise_type_id and exercise_id are selected
  // Numeric master-list exercises only; custom ones live in custom_list_values.exercises
  const cleanExercises =
    data.exercises && Array.isArray(data.exercises)
      ? data.exercises
          .filter(
            (e) =>
              e.exercise_id != null &&
              e.exercise_id !== '' &&
              String(e.exercise_id).trim() !== '' &&
              !Number.isNaN(Number(e.exercise_id)),
          )
          .map((e) => ({
            exercise_id: Number(e.exercise_id),
            note: e.note || '',
          }))
      : [];
  formData.append('exercises', JSON.stringify(cleanExercises));

  // Ingredients and preparation steps (for create/update with autofill or inline data)
  const ingredients = data.ingredients;
  if (ingredients && Array.isArray(ingredients) && ingredients.length > 0) {
    const cleanIngredients = ingredients
      .filter(
        (i) =>
          i &&
          (String(i.ingredient_name ?? i.name ?? '').trim() ||
            (i.quantity != null && i.quantity !== '') ||
            (i.unit != null && i.unit !== '')),
      )
      .map((i) => ({
        ingredient_name: String(i.ingredient_name ?? i.name ?? '').trim(),
        quantity: String(i.quantity ?? '').trim(),
        unit: String(i.unit ?? '').trim(),
      }));
    if (cleanIngredients.length > 0) {
      formData.append('ingredients', JSON.stringify(cleanIngredients));
    }
  }
  const preparationSteps = data.preparation_steps;
  if (
    preparationSteps &&
    Array.isArray(preparationSteps) &&
    preparationSteps.length > 0
  ) {
    const cleanSteps = preparationSteps
      .filter((s) => s && String(s.description ?? '').trim())
      .map((s, idx) => ({
        step_number: s.step_number ?? idx + 1,
        title: String(s.title ?? '').trim() || null,
        description: String(s.description ?? '').trim(),
      }));
    if (cleanSteps.length > 0) {
      formData.append('preparation_steps', JSON.stringify(cleanSteps));
    }
  }

  // Arrays/JSON
  // For JSON fields like suitability_json, backend expects them as fields?
  // They are complex objects, so sending as JSON string is standard.
  // Handle both string and array formats
  if (data.suitability_json) {
    if (typeof data.suitability_json === 'string') {
      appendIfExists(formData, 'suitability_json', data.suitability_json);
    } else if (Array.isArray(data.suitability_json)) {
      appendIfArrayNotEmpty(
        formData,
        'suitability_json',
        data.suitability_json,
      );
    }
  }
  if (data.common_mistakes_json) {
    if (typeof data.common_mistakes_json === 'string') {
      appendIfExists(
        formData,
        'common_mistakes_json',
        data.common_mistakes_json,
      );
    } else if (Array.isArray(data.common_mistakes_json)) {
      appendIfArrayNotEmpty(
        formData,
        'common_mistakes_json',
        data.common_mistakes_json,
      );
    }
  }
  if (data.q_and_a_json) {
    if (typeof data.q_and_a_json === 'string') {
      appendIfExists(formData, 'q_and_a_json', data.q_and_a_json);
    } else if (Array.isArray(data.q_and_a_json)) {
      appendIfArrayNotEmpty(formData, 'q_and_a_json', data.q_and_a_json);
    }
  }
  if (data.ref_links) {
    if (typeof data.ref_links === 'string') {
      appendIfExists(formData, 'ref_links', data.ref_links);
    } else if (Array.isArray(data.ref_links)) {
      appendIfArrayNotEmpty(formData, 'ref_links', data.ref_links);
    }
  }

  // Medical Condition Fields
  // medical_conditions_grid is usually passed as a JSON string from the wizard
  if (typeof data.medical_conditions_grid === 'string') {
    appendIfExists(
      formData,
      'medical_conditions_grid',
      data.medical_conditions_grid,
    );
  } else if (Array.isArray(data.medical_conditions_grid)) {
    appendIfArrayNotEmpty(
      formData,
      'medical_conditions_grid',
      data.medical_conditions_grid,
    );
  }

  // Add arrays based on format
  if (arrayFormat === 'json') {
    // POST: JSON stringified arrays
    appendIfArrayNotEmpty(formData, 'category_ids', data.category_ids);
    appendIfArrayNotEmpty(formData, 'dosha_ids', data.dosha_ids);
    appendIfArrayNotEmpty(formData, 'season', data.season);
    appendIfArrayNotEmpty(formData, 'season_ids', data.season_ids);
    appendIfArrayNotEmpty(formData, 'region_ids', data.region_ids);
    appendIfArrayNotEmpty(formData, 'gender_ids', data.gender_ids);
    appendIfArrayNotEmpty(formData, 'age_group_ids', data.age_group_ids);
    appendIfArrayNotEmpty(
      formData,
      'natural_condition_ids',
      data.natural_condition_ids,
    );
    // Always append helpsWithDiseases, even if empty array (to ensure it's not null)
    if (data.helpsWithDiseases && Array.isArray(data.helpsWithDiseases)) {
      if (data.helpsWithDiseases.length > 0) {
        appendIfArrayNotEmpty(
          formData,
          'helpsWithDiseases',
          data.helpsWithDiseases,
        );
        appendIfArrayNotEmpty(formData, 'symptom_ids', data.helpsWithDiseases); // Also support backend field name
      } else {
        // Send empty array as empty array, not null
        formData.append('helpsWithDiseases', JSON.stringify([]));
        formData.append('symptom_ids', JSON.stringify([]));
      }
    } else {
      // Ensure it's always sent as empty array, never null
      formData.append('helpsWithDiseases', JSON.stringify([]));
      formData.append('symptom_ids', JSON.stringify([]));
    }
    // Always ensure contra_indication_ids and contraindications are sent, even if empty
    if (
      data.contra_indication_ids &&
      Array.isArray(data.contra_indication_ids)
    ) {
      if (data.contra_indication_ids.length > 0) {
        appendIfArrayNotEmpty(
          formData,
          'contra_indication_ids',
          data.contra_indication_ids,
        );
      } else {
        formData.append('contra_indication_ids', JSON.stringify([]));
      }
    } else {
      formData.append('contra_indication_ids', JSON.stringify([]));
    }
    if (data.contraindications && Array.isArray(data.contraindications)) {
      if (data.contraindications.length > 0) {
        appendIfArrayNotEmpty(
          formData,
          'contraindications',
          data.contraindications,
        );
      } else {
        formData.append('contraindications', JSON.stringify([]));
      }
    } else {
      formData.append('contraindications', JSON.stringify([]));
    }
    appendIfArrayNotEmpty(formData, 'tags', data.tags);
  } else {
    const appendMultiForm = (key: string, arr?: unknown[]) => {
      if (arr && Array.isArray(arr)) {
        if (arr.length > 0) {
          formData.append(key, JSON.stringify(arr));
        } else {
          // When array is empty, send the base field as empty string so backend knows to clear the relationship
          formData.append(key, '');
        }
      }
    };

    // PUT: Multiple form fields with [] suffix
    appendMultiForm('dosha_ids', data.dosha_ids);
    appendMultiForm('category_ids', data.category_ids);
    appendMultiForm('season', data.season);
    appendMultiForm('season_ids', data.season_ids);
    appendMultiForm('region_ids', data.region_ids);
    appendMultiForm('gender_ids', data.gender_ids);
    appendMultiForm('age_group_ids', data.age_group_ids);
    appendMultiForm('natural_condition_ids', data.natural_condition_ids);

    // Always ensure helpsWithDiseases is sent, filtering out invalid values
    if (data.helpsWithDiseases && Array.isArray(data.helpsWithDiseases)) {
      const validSymptoms = data.helpsWithDiseases.filter(
        (s) => s !== 'other' && s !== null && s !== undefined && s !== '',
      );
      if (validSymptoms.length > 0) {
        formData.append('helpsWithDiseases', JSON.stringify(validSymptoms));
        formData.append('symptom_ids', JSON.stringify(validSymptoms)); // Also support backend field name
      } else {
        formData.append('helpsWithDiseases', '');
        formData.append('symptom_ids', '');
      }
    }

    // Always ensure contra_indication_ids and contraindications are sent, even if empty
    if (
      data.contra_indication_ids &&
      Array.isArray(data.contra_indication_ids)
    ) {
      const validContraIndicationIds = data.contra_indication_ids.filter(
        (c) => c !== null && c !== undefined && c !== '',
      );
      if (validContraIndicationIds.length > 0) {
        formData.append(
          'contra_indication_ids',
          JSON.stringify(validContraIndicationIds),
        );
      } else {
        formData.append('contra_indication_ids', '');
      }
    }

    if (data.contraindications && Array.isArray(data.contraindications)) {
      const validContraindications = data.contraindications.filter(
        (c) => c !== null && c !== undefined && c !== '',
      );
      if (validContraindications.length > 0) {
        formData.append(
          'contraindications',
          JSON.stringify(validContraindications),
        );
      } else {
        formData.append('contraindications', '');
      }
    }

    if (data.tags && Array.isArray(data.tags)) {
      if (data.tags.length > 0) {
        formData.append('tags', JSON.stringify(data.tags));
      } else {
        formData.append('tags', '');
      }
    }
  }

  // Add numeric fields
  // Backend expects 'prep_time_minutes' for PUT, but 'preparation_time' for POST
  if (arrayFormat === 'form-fields') {
    // PUT: Use backend field name
    appendIfExists(formData, 'prep_time_minutes', data.preparation_time);
  } else {
    // POST: Use frontend field name
    appendIfExists(formData, 'preparation_time', data.preparation_time);
  }
  appendIfExists(formData, 'servings', data.servings);
  appendIfExists(formData, 'calorie', data.calorie);

  // Debug: Log what was appended for contraindications
  console.log('🔍 DEBUG buildRecipeFormData - After append:', {
    hasContraIndicationIds:
      formData.has('contra_indication_ids') ||
      formData.has('contra_indication_ids[]'),
    hasContraindications:
      formData.has('contraindications') || formData.has('contraindications[]'),
    arrayFormat,
  });

  return formData;
};

/**
 * Builds FormData for preparation step create/update operations
 * @param step - Step data including files
 * @param stepId - Optional step ID for update operations
 * @returns FormData object ready to send
 */
export const buildStepFormData = (
  step: {
    step_number: number;
    title: string;
    description: string;
    image?: File;
    video?: File;
  },
  stepId?: string | number,
): FormData => {
  const formData = new FormData();

  // Add step_id for update operations
  if (stepId !== undefined) {
    formData.append('id', String(stepId));
  }

  // Add step data (backend expects: step_number, title, description)
  formData.append('step_number', String(step.step_number));
  formData.append('title', step.title ?? ''); // Additional Notes - optional
  formData.append('description', step.description);

  // Add files using helper (backend expects 'step_image' and 'step_video')
  appendIfExists(formData, 'step_image', step.image);
  appendIfExists(formData, 'step_video', step.video);

  // Add keys for deletion
  // These should be passed in the step object
  if ('delete_image' in step && step.delete_image) {
    formData.append('delete_image', 'true');
  }
  if ('delete_video' in step && step.delete_video) {
    formData.append('delete_video', 'true');
  }

  return formData;
};
